// FieldDesk — обёртка над глобальным BX24 API.
//
// Б24 в iframe-placement'е экспортирует `window.BX24` (грузится скриптом
// //api.bitrix24.com/api/v1/). В standalone-режиме объекта нет — тогда все
// хелперы возвращают null, а UI показывает standalone-ветку.
//
// Мы намеренно НЕ используем @bitrix24/b24jssdk: обёртка тривиальная, внешний
// пакет принёс бы лишний бандл-размер и риски версионирования без пользы.

import type { BitrixAuthParams } from '../api/types';

interface BX24AuthData {
  access_token?: string;
  AUTH_ID?: string;
  member_id?: string;
  domain?: string;
  DOMAIN?: string;
  PLACEMENT?: string;
  PLACEMENT_OPTIONS?: string;
}

interface BX24PlacementInfo {
  placement: string;
  options: Record<string, unknown>;
}

interface BX24Global {
  init(cb: () => void): void;
  getAuth(): BX24AuthData;
  placement: {
    info(): BX24PlacementInfo;
  };
  resizeWindow(w: number, h: number): void;
  closeApplication(): void;
  // Открывает внутренний путь Б24 (например, карточку компании) в slider'е
  // того же портала. Возвращает true если URL соответствует домену портала.
  openPath?(path: string, cb?: () => void): boolean;
  // Запасной — открывает произвольную ссылку в новой вкладке.
  openLink?(url: string): void;
}

declare global {
  interface Window {
    BX24?: BX24Global;
  }
}

export function isEmbedded(): boolean {
  // В iframe'е Б24 обычно `window.parent !== window`, плюс объект BX24 на месте.
  if (typeof window === 'undefined') return false;
  if (!window.BX24) return false;
  try {
    return window.parent !== window;
  } catch {
    return true;
  }
}

/** Promise-обёртка над `BX24.init()`. Ресолвится, когда SDK инициализирован. */
export function waitForBX24(timeoutMs = 5000): Promise<BX24Global> {
  return new Promise((resolve, reject) => {
    if (!window.BX24) {
      reject(new Error('BX24 SDK не загружен — не-embedded окружение'));
      return;
    }
    const bx = window.BX24;
    const timer = setTimeout(() => reject(new Error('BX24.init timeout')), timeoutMs);
    bx.init(() => {
      clearTimeout(timer);
      resolve(bx);
    });
  });
}

export interface PlacementContext {
  placement: string;
  dealId: string | null;
  raw: BX24PlacementInfo;
}

/** Читаем placement-контекст. Для `CRM_DEAL_DETAIL_TAB`/`CRM_DEAL_LIST_MENU`
 *  Б24 кладёт ID сделки в `options.ID`. */
export function readPlacement(bx: BX24Global): PlacementContext {
  const info = bx.placement.info();
  const opts = (info.options ?? {}) as Record<string, unknown>;
  const rawId = opts.ID ?? opts.id ?? null;
  const dealId = rawId != null && String(rawId).trim() !== '' ? String(rawId) : null;
  return { placement: info.placement, dealId, raw: info };
}

/**
 * Открывает карточку компании Б24 в slider'е портала.
 * В standalone-режиме (нет BX24) — no-op (вызывающий код прячет кнопку).
 */
export function openBitrixCompany(companyId: string): void {
  const bx = window.BX24;
  const path = `/crm/company/details/${companyId}/`;
  if (bx?.openPath) {
    try {
      bx.openPath(path);
      return;
    } catch {
      /* fall through */
    }
  }
  // Standalone — у нас нет домена портала; в embedded openPath сработает.
  // Если не embedded — просто ничего не делаем.
}

/** Преобразуем `BX24.getAuth()` в form-совместимый payload для backend. */
export function readAuthParams(bx: BX24Global): BitrixAuthParams | null {
  const a = bx.getAuth();
  const authId = a.AUTH_ID ?? a.access_token;
  const domain = a.DOMAIN ?? a.domain;
  const memberId = a.member_id;
  if (!authId || !domain || !memberId) return null;
  return {
    AUTH_ID: authId,
    member_id: memberId,
    DOMAIN: domain,
    ...(a.PLACEMENT ? { PLACEMENT: a.PLACEMENT } : {}),
    ...(a.PLACEMENT_OPTIONS ? { PLACEMENT_OPTIONS: a.PLACEMENT_OPTIONS } : {}),
  };
}
